/**
 * Resilient EventSource wrapper with automatic session-cookie refresh and
 * exponential backoff reconnection.
 *
 * Replaces raw `new EventSource()` usage throughout the app.
 * Both the workspace agent stream and the notification stream use this
 * so reconnection / refresh logic lives in one place.
 *
 * Authentication model: EventSource is constructed with
 * `withCredentials: true` so the browser attaches the HttpOnly
 * `migratr_access` cookie automatically.  The URL no longer needs to
 * carry a `?token=` query parameter.
 */
import { tryRefreshToken } from '../services/apiClient';

export interface ResilientEventSourceOptions {
  /** Called on each (re)connect to build the URL.  No tokens required — the
   *  browser attaches auth cookies automatically. */
  urlBuilder: () => string;
  /** Default message handler (unnamed events). */
  onMessage?: (event: MessageEvent) => void;
  /** Fired on first successful connection. */
  onConnected?: () => void;
  /** Fired on every disconnect (before reconnect attempt). */
  onDisconnected?: () => void;
  /**
   * Fired when a reconnection succeeds (not on the initial connect).
   * Use this to invalidate caches and recover missed events.
   */
  onReconnected?: () => void;
  /** Named event listeners (e.g. { 'agent_event': handler, 'heartbeat': handler }). */
  eventListeners?: Record<string, (e: MessageEvent) => void>;
  /** Max consecutive reconnection failures before giving up. Default 10. */
  maxRetries?: number;
}

export interface ResilientEventSourceHandle {
  /** Permanently close the connection (no more reconnects). */
  close: () => void;
}

export function createResilientEventSource(
  options: ResilientEventSourceOptions,
): ResilientEventSourceHandle {
  const {
    urlBuilder,
    onMessage,
    onConnected,
    onDisconnected,
    onReconnected,
    eventListeners,
    maxRetries = 10,
  } = options;

  let es: EventSource | null = null;
  let closed = false;
  let retryCount = 0;
  let hasConnectedBefore = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function connect() {
    if (closed) return;

    // Close any lingering connection
    if (es) {
      es.close();
      es = null;
    }

    const url = urlBuilder();
    // withCredentials ensures the HttpOnly auth cookie is attached — required
    // because /stream endpoints run JWTAuthMiddleware like every other route.
    es = new EventSource(url, { withCredentials: true });

    es.onopen = () => {
      const wasReconnect = hasConnectedBefore;
      hasConnectedBefore = true;
      retryCount = 0;

      if (wasReconnect) {
        onReconnected?.();
      } else {
        onConnected?.();
      }
    };

    if (onMessage) {
      es.onmessage = onMessage;
    }

    // Attach named event listeners
    if (eventListeners) {
      for (const [eventName, handler] of Object.entries(eventListeners)) {
        es.addEventListener(eventName, handler as EventListener);
      }
    }

    es.onerror = () => {
      if (closed) return;

      // Close the broken connection
      es?.close();
      es = null;

      onDisconnected?.();

      // Attempt reconnection with exponential backoff
      retryCount++;
      if (retryCount > maxRetries) {
        // Give up — caller handles via onDisconnected
        return;
      }

      const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 30000);

      retryTimer = setTimeout(async () => {
        if (closed) return;

        // Try refreshing the session cookie before reconnecting.  This
        // catches the case where the access cookie expired while SSE was
        // open.  On failure we still attempt the reconnect — the server
        // will 401 cleanly and the caller's onDisconnected handler fires.
        try {
          await tryRefreshToken();
        } catch {
          /* noop */
        }

        connect();
      }, delay);
    };
  }

  // Start the initial connection
  connect();

  return {
    close() {
      closed = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (es) {
        es.close();
        es = null;
      }
    },
  };
}
